import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import type { ResolvedBy } from 'api/recoveryAi.types'
import DetailPanel from 'components/DetailPanel'
import FeedbackRatingIcon, {
  FeedbackRating,
  RESPONSE_COLUMN_WIDTH_PX,
} from 'components/FeedbackRatingIcon'
import type { SurfaceVariant } from 'components/orderFeedbackSurface'
import SortableColumnHeader, {
  SortDirection,
} from 'components/SortableColumnHeader'
import TooltipLabel from 'components/TooltipLabel'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { translateEnum } from 'utilities/translateEnum'
import ResolvedByCell from './ResolvedByCell'

const t = buildTranslate('components.recent_activities')

// The initial slice shown before the "View N more" expander (Figma). Not a
// hard cap anymore — expanding reveals every row already fetched by the
// caller (no new query).
const INITIAL_ROW_COUNT = 5

// Distinct per mount, so the expander's aria-controls points at THIS table's
// rows even when two of these panels render on one page. Pre-useId (React 16),
// so a module counter stands in — see `rowsRegionId`.
let instanceCount = 0

// Frontend resolution enums (D19). Moved here from the case detail's
// knownEnums.ts when this table was promoted — it is the only consumer, and a
// shared component must not import from a scene. `resolved` is the generic
// outcome the order-rating surface passes (its rows carry only who resolved
// the rating, not how).
const KNOWN_RESOLUTION_TYPES: ReadonlySet<string> = new Set([
  'resolved_with_reward',
  'resolved_without_reward',
  'no_action',
  'resolved',
  // Neutral outcome for an operator's close on the who-resolved surface — reads
  // "Closed", not "Resolved", so it agrees with the header badge and timeline.
  'closed_by_operator',
])

// One prior resolved record for this customer. Surface-agnostic: the case
// detail feeds it a prior CASE (api RecentActivity), the Reports order-rating
// detail a prior RATING. `viewTo` / `viewAriaLabel` are the only per-surface
// bits, so each caller supplies its own destination and accessible name rather
// than this component knowing about routes.
export type RecentActivityRow = {
  id: string
  date: string
  // Frontend resolution enum. Null when the record was never resolved (an
  // order rating can be) → the em-dash placeholder.
  resolutionType: string | null
  resolvedBy: ResolvedBy
  // Optional: the case surface renders the per-row reward NAME in place of the
  // enum label; the order-rating surface has no reward name.
  rewardName?: string | null
  // The row's own thumb rating, feeding the Response column glyph. Always
  // populated: a RAI case can't exist without the negative rating that
  // created it, so CaseDetail's caller passes a constant 'down' (mirrors
  // FeedbackTableRow's identical "W3 always passes 'down'" comment).
  rating: FeedbackRating
  // Return-in-30-days column (redesign phase 4). The wire field ships in #3981
  // (`returned_within_30_days`, mapped in api/orderRatings.ts); the column stays
  // hidden until a CALLER forwards it (the Reports scene does in #3984 — RAI
  // never has the field). `undefined` = "this caller doesn't supply it" and hides
  // the whole column; `null` = "supplied, no verdict yet"; `true`/`false` is the
  // verdict. Gate on presence (`!== undefined`), never truthiness — see
  // `showReturnColumn` below.
  returnedWithin30Days?: boolean | null
  viewTo: string
  viewAriaLabel: string
}

type Props = {
  rows: RecentActivityRow[]
  formatDate: (iso: string | null) => string
  // Optional per-row Date-column tooltip. Surface-specific (the RAI case
  // detail's Date column shows a prior CASE date, not a feedback submission),
  // so each caller supplies its own copy; omitted → no tooltip icon.
  dateTooltip?: string
  // Which detail surface this panel renders on. Collapses the old
  // largeHeading / blankResolvedByWhenNameless booleans onto one axis: the RAI
  // case detail (default look) vs the Reports order-rating redesign, which
  // renders the title at Header4 and blanks a nameless Resolved-by cell (Option
  // B, derived inside ResolvedByCell). Defaults to 'case-detail'.
  variant?: SurfaceVariant
}

type SortField = 'date' | 'resolution'

// Shared by rendering AND sorting so the two never disagree. Use `||` (not
// `??`) so a blank reward name ('') also falls through to the enum label
// rather than rendering the em-dash placeholder.
const resolutionTextFor = (row: RecentActivityRow): string =>
  row.rewardName ||
  translateEnum(
    KNOWN_RESOLUTION_TYPES,
    row.resolutionType,
    t,
    'resolution_types',
    t('empty')
  )

const RETURN_ICON_SIZE_PX = 20

// The gated Return-in-30-days cell renders a status GLYPH (Figma), not text:
// green check = returned, orange ✕ = did not, grey hollow circle = pending
// (null verdict). `undefined` returns no glyph — the column is hidden unless
// some row carries the field, and an individual undefined row shows the
// em-dash. Early-return lookup, not a nested ternary (a review rejection).
type ReturnGlyph = { icon: string; color: string; labelKey: string }
const returnGlyphFor = (
  value: boolean | null | undefined
): ReturnGlyph | null => {
  if (value === true)
    return {
      icon: 'fa-check-circle',
      color: merchantTheme.colors.nephritis50,
      labelKey: 'return_30_days.yes',
    }
  if (value === false)
    return {
      icon: 'fa-times-circle',
      color: merchantTheme.colors.cayenne50,
      labelKey: 'return_30_days.no',
    }
  if (value === null)
    return {
      icon: 'fa-circle-o',
      color: merchantTheme.colors.grey40,
      labelKey: 'return_30_days.pending',
    }
  return null
}

// The "Recent activities" panel (card chrome + table) shared by the
// RecoveryAI case detail and the Reports order-rating detail page. Renders
// nothing at all when there are no rows, so neither page has to gate it.
const RecentActivitiesTable = ({
  rows,
  formatDate,
  dateTooltip,
  variant = 'case-detail',
}: Props) => {
  const [css] = useStyletron()
  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [rowsRegionId] = useState(
    () => `recent-activities-rows-${(instanceCount += 1)}`
  )

  const largeHeading = variant === 'order-rating'

  // Sort + expansion reset on record change is the CALLER'S responsibility, via a
  // React `key` tied to the record id (`<RecentActivitiesTable key={recordId} … />`).
  // A new record remounts this table (clearing sort/expansion); a same-record
  // background refetch (RTK tag invalidation → a fresh `rows` array) keeps the key
  // stable, so the user's sort/expansion survives. Do NOT reset off `rows` identity
  // — that over-fires on every same-record refetch.

  const sortedRows = useMemo(() => {
    if (!sortField || !sortDirection) return rows
    const ascending =
      sortField === 'date'
        ? (a: RecentActivityRow, b: RecentActivityRow) =>
            a.date.localeCompare(b.date)
        : (a: RecentActivityRow, b: RecentActivityRow) =>
            resolutionTextFor(a).localeCompare(resolutionTextFor(b))
    // Flip the comparator (not `.reverse()`) for descending: Array.sort is
    // stable, so equal-key rows keep their original relative order in BOTH
    // directions — `.reverse()` would flip ties too.
    const compare =
      sortDirection === 'asc'
        ? ascending
        : (a: RecentActivityRow, b: RecentActivityRow) => ascending(b, a)
    return [...rows].sort(compare)
  }, [rows, sortField, sortDirection])

  // Presence, not truthiness: a real `false` value must still show the
  // column, so only TOTAL absence of the field across every row hides it.
  //
  // That absence is today's reality on BOTH callers, and on the order-rating
  // one it is now a deliberate hold rather than a missing backend: the route
  // does send a verdict, but it is not trusted yet, so
  // useRatingCustomerPanels withholds the field (see the comment there). This
  // column is therefore dead in the UI until that hold lifts (AI-1058) — kept,
  // not deleted, precisely so lifting it is a one-line change.
  const showReturnColumn = useMemo(
    () => rows.some(row => row.returnedWithin30Days !== undefined),
    [rows]
  )

  const headerCellStyle = {
    padding: '12px 16px',
    fontWeight: 700,
    // Match the sortable headers (SortableColumnHeader = 14px / grey90) so the
    // plain and sortable column headers read uniformly, per the mockup.
    fontSize: '14px',
    color: merchantTheme.colors.grey90,
    borderBottom: `1px solid ${merchantTheme.colors.grey20}`,
    textAlign: 'left',
  } as const

  const headerCellClass = css(headerCellStyle)

  const cellClass = css({
    padding: '12px 16px',
    fontSize: '14px',
    color: merchantTheme.colors.grey90,
    borderBottom: `1px solid ${merchantTheme.colors.grey10}`,
    verticalAlign: 'middle',
  })

  // Response column: header AND cell are both centered, so the glyph sits
  // exactly under the "Response" label however the browser distributes width —
  // belt and braces with the pinned <col> width below. Declared here rather
  // than composed inline in the JSX so the centering lives in one place.
  //
  // The header overrides textAlign by re-declaring the whole style object rather
  // than concatenating a second atomic class (mirrors FeedbackTable): the base
  // header already declares `text-align: left`, and two competing atomic classes
  // are resolved by styletron's injection order, not by the order they appear in
  // `className`. The cell has no textAlign of its own, so there it can compose.
  const responseHeaderCellClass = css({
    ...headerCellStyle,
    textAlign: 'center',
  })
  const responseCellClass = `${cellClass} ${css({ textAlign: 'center' })}`

  if (rows.length === 0) return null

  // Only one column is ever the active sort — clicking a column always resets
  // it to ascending, whichever column was previously active goes back to
  // unsorted (mirrors FeedbackTable's asc/desc toggle-on-click convention).
  const handleSort = (field: SortField) => {
    if (sortField !== field) {
      setSortField(field)
      setSortDirection('asc')
      return
    }
    setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
  }

  const visibleRows = isExpanded
    ? sortedRows
    : sortedRows.slice(0, INITIAL_ROW_COUNT)
  const hiddenCount = sortedRows.length - INITIAL_ROW_COUNT
  const hasMore = hiddenCount > 0

  return (
    <DetailPanel>
      {largeHeading ? (
        <Text.Header4>{t('heading')}</Text.Header4>
      ) : (
        <Text.BodyText4 bold>{t('heading')}</Text.BodyText4>
      )}
      <div className={css({ marginTop: '12px' })}>
        <table className={css({ width: '100%', borderCollapse: 'collapse' })}>
          <caption className="sr-only">{t('heading')}</caption>
          {/* Keep this <col> list in sync with the <th> list below — same count,
              same order, including the conditional Return-in-30-days column. A
              colgroup shorter or longer than the header row silently shifts the
              pinned width onto the wrong column.
              Layout stays `auto` (NOT `fixed`): only the Response column is
              pinned, and the remaining <col>s are left unsized so they absorb
              the leftover width exactly as they do today. Going `fixed` would
              force a width onto every column and reflow the RAI case-detail
              surface (scenes/RecoveryAI/CaseDetail/OverviewTab). */}
          <colgroup>
            <col className={css({ width: `${RESPONSE_COLUMN_WIDTH_PX}px` })} />
            {/* Date */}
            <col />
            {/* Resolved by */}
            <col />
            {/* Resolution */}
            <col />
            {/* Return in 30 days — same guard as its <th> */}
            {showReturnColumn && <col />}
            {/* View */}
            <col />
          </colgroup>
          <thead>
            <tr>
              <th scope="col" className={responseHeaderCellClass}>
                {t('response')}
              </th>
              <SortableColumnHeader
                label={t('date')}
                sortAriaLabel={t('sort_by_date')}
                direction={sortField === 'date' ? sortDirection : null}
                onSort={() => handleSort('date')}
                className={headerCellClass}
              />
              <th scope="col" className={headerCellClass}>
                {t('resolved_by')}
              </th>
              <SortableColumnHeader
                label={t('resolution')}
                sortAriaLabel={t('sort_by_resolution')}
                direction={sortField === 'resolution' ? sortDirection : null}
                onSort={() => handleSort('resolution')}
                className={headerCellClass}
              />
              {showReturnColumn && (
                <th scope="col" className={headerCellClass}>
                  {t('return_30_days.heading')}
                </th>
              )}
              <th scope="col" className={headerCellClass}>
                <span className="sr-only">{t('view')}</span>
              </th>
            </tr>
          </thead>
          <tbody id={rowsRegionId}>
            {visibleRows.map(row => {
              const returnGlyph = returnGlyphFor(row.returnedWithin30Days)
              return (
                <tr key={row.id}>
                  <td className={responseCellClass}>
                    <FeedbackRatingIcon rating={row.rating} />
                  </td>
                  <td className={cellClass}>
                    {dateTooltip ? (
                      <TooltipLabel
                        label={
                          <Text.BodyText4 tag="span" color="grey90">
                            {formatDate(row.date)}
                          </Text.BodyText4>
                        }
                        tooltip={dateTooltip}
                      />
                    ) : (
                      <Text.BodyText4 color="grey90">
                        {formatDate(row.date)}
                      </Text.BodyText4>
                    )}
                  </td>
                  <td className={cellClass}>
                    <ResolvedByCell
                      resolvedBy={row.resolvedBy}
                      variant={variant}
                    />
                  </td>
                  <td className={cellClass}>
                    <Text.BodyText4 color="grey90">
                      {resolutionTextFor(row) || t('empty')}
                    </Text.BodyText4>
                  </td>
                  {showReturnColumn && (
                    <td className={cellClass}>
                      {returnGlyph ? (
                        <i
                          className={`fa ${returnGlyph.icon} ${css({
                            fontSize: `${RETURN_ICON_SIZE_PX}px`,
                            color: returnGlyph.color,
                          })}`}
                          role="img"
                          aria-label={t(returnGlyph.labelKey)}
                        />
                      ) : (
                        // `undefined` on a shown column = this row lacks the
                        // field → em-dash, distinct from the null "pending"
                        // circle above.
                        <Text.BodyText4 color="grey90">
                          {t('empty')}
                        </Text.BodyText4>
                      )}
                    </td>
                  )}
                  <td className={cellClass}>
                    {/* Always an internal <Link>, never a dead button (a11y
                        rule). The destination is the caller's: the prior case on
                        the RAI surface, the prior rating on the Reports one. */}
                    <Link
                      to={row.viewTo}
                      aria-label={row.viewAriaLabel}
                      className={css({
                        fontWeight: 600,
                        textDecoration: 'none',
                        color: palette.belizeHole,
                      })}
                    >
                      {t('view')}
                    </Link>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {hasMore && (
          <div className={css({ marginTop: '12px' })}>
            {/* A real disclosure toggle: aria-expanded tracks the state and
                aria-controls names the rows it reveals, so the collapsed state
                still matches the mockup (down chevron + "View N more"). */}
            <Button
              kind="minimal"
              aria-expanded={isExpanded}
              aria-controls={rowsRegionId}
              onClick={() => setIsExpanded(prev => !prev)}
            >
              <span
                className={`d-flex align-items-center ${css({ gap: '6px' })}`}
              >
                {isExpanded
                  ? t('view_less')
                  : t('view_more', { count: hiddenCount })}
                <i
                  className={`fa ${
                    isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'
                  }`}
                  aria-hidden="true"
                />
              </span>
            </Button>
          </div>
        )}
      </div>
    </DetailPanel>
  )
}

export default RecentActivitiesTable
